import { useState } from 'react';
import { useAppStore } from '@/lib/store';
import { heightSchema } from '@/lib/validators';
import { StepProgress } from './ui/StepProgress';

const STEPS = ['Clothing', 'Height', 'Photo', 'Results'];

type Unit = 'cm' | 'ft';

export function ReferenceSelector() {
  const { userName, setUserName, userHeightCm, setUserHeightCm, setStep, gender, setGender } =
    useAppStore();

  const [unit, setUnit] = useState<Unit>('cm');
  const [inputValue, setInputValue] = useState(
    userHeightCm ? String(userHeightCm) : ''
  );
  const [feetValue, setFeetValue] = useState('');
  const [inchesValue, setInchesValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  function cmFromFeetInches(feet: string, inches: string): number {
    const ft = parseFloat(feet) || 0;
    const inc = parseFloat(inches) || 0;
    return Math.round((ft * 30.48 + inc * 2.54) * 10) / 10;
  }

  function handleSubmit() {
    let heightCm: number;

    if (unit === 'cm') {
      heightCm = parseFloat(inputValue);
    } else {
      heightCm = cmFromFeetInches(feetValue, inchesValue);
    }

    if (Number.isNaN(heightCm)) {
      setError('Please enter your height');
      return;
    }

    const result = heightSchema.safeParse(heightCm);
    if (!result.success) {
      setError(result.error.issues?.[0]?.message ?? 'Invalid height');
      return;
    }

    setError(null);
    setUserHeightCm(result.data);
    setStep('capture_session');
  }

  return (
    <div className="screen">
      <StepProgress steps={STEPS} currentIndex={1} />

      <div className="mt-6 flex-1">
        <h2 className="text-2xl font-bold text-gray-900">
          About you
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          This helps us calibrate measurements and personalize your results.
        </p>

        {/* Name input */}
        <div className="mt-6">
          <label className="text-sm font-medium text-gray-700">
            Your name <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <input
            type="text"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            onBlur={(e) => setUserName(e.target.value.trim())}
            placeholder="Enter your name"
            maxLength={50}
            className="mt-2 w-full rounded-xl border border-gray-300 px-4 py-3 text-base focus:border-brand-500 focus:ring-brand-500"
          />
        </div>

        {/* Gender selection */}
        <div className="mt-6">
          <label className="text-sm font-medium text-gray-700">Gender</label>
          <div className="mt-2 flex gap-3">
            {(['male', 'female', 'other'] as const).map((g) => (
              <button
                key={g}
                onClick={() => setGender(g)}
                className={`flex-1 rounded-lg border-2 px-3 py-2.5 text-sm font-medium capitalize transition-all ${
                  gender === g
                    ? 'border-brand-500 bg-brand-50 text-brand-700'
                    : 'border-gray-200 bg-white text-gray-600'
                }`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>

        {/* Unit toggle */}
        <div className="mt-6">
          <label className="text-sm font-medium text-gray-700">Height</label>
          <div className="mt-2 flex rounded-lg bg-gray-100 p-1">
            <button
              onClick={() => setUnit('cm')}
              className={`flex-1 rounded-md py-2 text-sm font-medium transition-colors ${
                unit === 'cm'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500'
              }`}
            >
              Centimeters
            </button>
            <button
              onClick={() => setUnit('ft')}
              className={`flex-1 rounded-md py-2 text-sm font-medium transition-colors ${
                unit === 'ft'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500'
              }`}
            >
              Feet & Inches
            </button>
          </div>
        </div>

        {/* Height input */}
        <div className="mt-4">
          {unit === 'cm' ? (
            <div className="relative">
              <input
                type="number"
                inputMode="numeric"
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  setError(null);
                }}
                placeholder="175"
                min={50}
                max={250}
                className="w-full rounded-xl border border-gray-300 px-4 py-4 pr-12 text-lg focus:border-brand-500 focus:ring-brand-500"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                cm
              </span>
            </div>
          ) : (
            <div className="flex gap-3">
              <div className="relative flex-1">
                <input
                  type="number"
                  inputMode="numeric"
                  value={feetValue}
                  onChange={(e) => {
                    setFeetValue(e.target.value);
                    setError(null);
                  }}
                  placeholder="5"
                  min={1}
                  max={8}
                  className="w-full rounded-xl border border-gray-300 px-4 py-4 pr-8 text-lg focus:border-brand-500 focus:ring-brand-500"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                  ft
                </span>
              </div>
              <div className="relative flex-1">
                <input
                  type="number"
                  inputMode="numeric"
                  value={inchesValue}
                  onChange={(e) => {
                    setInchesValue(e.target.value);
                    setError(null);
                  }}
                  placeholder="9"
                  min={0}
                  max={11}
                  className="w-full rounded-xl border border-gray-300 px-4 py-4 pr-8 text-lg focus:border-brand-500 focus:ring-brand-500"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                  in
                </span>
              </div>
            </div>
          )}

          {error && (
            <p className="mt-2 text-sm text-red-500">{error}</p>
          )}

          {/* Quick height presets */}
          <div className="mt-3 flex flex-wrap gap-2">
            {[155, 160, 165, 170, 175, 180, 185].map((h) => (
              <button
                key={h}
                onClick={() => {
                  setInputValue(String(h));
                  setUnit('cm');
                  setError(null);
                }}
                className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs text-gray-600 transition-colors hover:border-brand-300 hover:text-brand-600"
              >
                {h} cm
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 space-y-3">
        <button onClick={handleSubmit} className="btn-primary">
          Continue
        </button>
        <button onClick={() => setStep('clothing')} className="btn-secondary">
          Back
        </button>
      </div>
    </div>
  );
}
